import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { pesquisarRecebimentos, pesquisarProdutos, mensagemErro } from '../tributosApi';
import { formatarCnpj } from '../util';

function Kpi({ titulo, valor, icone, tom, onClick, rodape }) {
  return (
    <button type="button" className={`tb-kpi tb-kpi-${tom}`} onClick={onClick} disabled={!onClick}>
      <span className="tb-kpi-icon"><i className={icone} /></span>
      <span className="tb-kpi-body">
        <small>{titulo}</small>
        <strong>{valor === null || valor === undefined ? '—' : Number(valor).toLocaleString('pt-BR')}</strong>
        {rodape ? <em>{rodape}</em> : null}
      </span>
    </button>
  );
}

Kpi.propTypes = {
  titulo: PropTypes.string.isRequired,
  valor: PropTypes.number,
  icone: PropTypes.string.isRequired,
  tom: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  rodape: PropTypes.string,
};

Kpi.defaultProps = { valor: null, onClick: null, rodape: null };

export default function Painel({ resumo, onAtualizar }) {
  const navigate = useNavigate();
  const [fila, setFila] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [ean, setEan] = useState('');

  useEffect(() => {
    onAtualizar();
    pesquisarRecebimentos({ status: 'PENDENTE' })
      .then(l => setFila(l.slice(0, 8)))
      .catch(e => toast.error(mensagemErro(e)))
      .finally(() => setCarregando(false));
  }, [onAtualizar]);

  const consultarEan = async e => {
    e.preventDefault();
    const codigo = ean.replace(/\s/g, '');
    if (!codigo) return;
    try {
      const lista = await pesquisarProdutos({ codigoEan: codigo, exact: 'S' });
      if (lista.length) {
        navigate(`/tributos/produtos/${lista[0].id}`);
      } else {
        toast.info('EAN não encontrado na base de tributos.');
      }
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };

  const percentualConferido = resumo && resumo.totalProdutos
    ? Math.round((resumo.produtosConferidos / resumo.totalProdutos) * 100)
    : 0;

  return (
    <div className="tb-page">
      <div className="tb-page-head">
        <div>
          <h1>Painel</h1>
          <p>Acompanhe os produtos recebidos da Retaguarda e a situação da base de tributos.</p>
        </div>
        <form className="tb-search-inline" onSubmit={consultarEan}>
          <i className="pi pi-search" />
          <input value={ean} onChange={e => setEan(e.target.value)} placeholder="Consultar EAN…" inputMode="numeric" />
        </form>
      </div>

      <div className="tb-kpis">
        <Kpi titulo="Aguardando conferência" valor={resumo && resumo.pendentes} icone="pi pi-clock" tom="warn" onClick={() => navigate('/tributos/recebimentos')} />
        <Kpi titulo="Recebidos hoje" valor={resumo && resumo.recebidosHoje} icone="pi pi-inbox" tom="info" />
        <Kpi titulo="Conferidos hoje" valor={resumo && resumo.conferidosHoje} icone="pi pi-check-circle" tom="ok" />
        <Kpi
          titulo="Base de tributos"
          valor={resumo && resumo.totalProdutos}
          icone="pi pi-database"
          tom="neutral"
          onClick={() => navigate('/tributos/produtos')}
          rodape={resumo ? `${percentualConferido}% conferidos` : null}
        />
        {resumo && resumo.validadeConferenciaDias > 0 ? (
          <Kpi
            titulo="Conferências vencidas"
            valor={resumo.conferenciasVencidas}
            icone="pi pi-exclamation-triangle"
            tom="danger"
            rodape={`Validade: ${resumo.validadeConferenciaDias} dias`}
          />
        ) : null}
      </div>

      <section className="tb-card">
        <div className="tb-card-head">
          <h2>Próximos da fila</h2>
          <button type="button" className="tb-btn tb-btn-link" onClick={() => navigate('/tributos/recebimentos')}>
            Ver todos <i className="pi pi-arrow-right" />
          </button>
        </div>
        {carregando ? <div className="tb-empty"><i className="pi pi-spin pi-spinner" /> Carregando…</div> : null}
        {!carregando && fila.length === 0 ? (
          <div className="tb-empty">
            <i className="pi pi-check-circle" />
            <span>Nenhum produto aguardando conferência.</span>
          </div>
        ) : null}
        <ul className="tb-queue">
          {fila.map(r => (
            <li key={r.id}>
              <button type="button" onClick={() => navigate(`/tributos/conferencia/${r.id}`)}>
                <span className="tb-queue-ean">{r.codigoEan || 'Sem EAN'}</span>
                <span className="tb-queue-desc">
                  <strong>{r.descricao}</strong>
                  <small>
                    {r.nomeEmpresa || formatarCnpj(r.cnpjEmpresa) || 'Retaguarda'}
                    {r.numeroNotaFiscal ? ` · NF ${r.numeroNotaFiscal}` : ''}
                    {' · '}
                    {r.dataHoraRecebimento}
                  </small>
                </span>
                {r.quantidadeEnvios > 1 ? <span className="tb-tag tb-tag-muted">{r.quantidadeEnvios} envios</span> : null}
                <i className="pi pi-chevron-right" />
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

Painel.propTypes = {
  resumo: PropTypes.shape({
    pendentes: PropTypes.number,
    recebidosHoje: PropTypes.number,
    conferidosHoje: PropTypes.number,
    totalProdutos: PropTypes.number,
    produtosConferidos: PropTypes.number,
    conferenciasVencidas: PropTypes.number,
    validadeConferenciaDias: PropTypes.number,
  }),
  onAtualizar: PropTypes.func.isRequired,
};

Painel.defaultProps = { resumo: null };
